import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../environments/environment';
import {
  ListenErkennung,
  ProjektListenTyp,
  SpsAnlagenteilOptionen,
  SpsAnlagenteilSpalte,
  SpsEintragLoeschvorschau,
  SpsImportEintrag,
  SpsImportErgebnis,
  SpsImportListe,
  SpsImportListeDetail,
  SpsImportVorschau,
  SpsListeLoeschErgebnis,
  SpsListeLoeschvorschau,
  SpsListenFilter,
  SpsListenUpdateErgebnis,
  SpsListenUpdateVorschau,
  SpsLoeschErgebnis,
  leererSpsListenFilter,
  SpsSammelImportDatei,
  SpsSammelImportErgebnis,
  SpsSeite
} from '../Models/Sps';

/**
 * SPS-Import (Backend: SpsImportController, Basisroute `/api/sps`).
 *
 * Erkennung, Vorschau und Import laufen als Multipart-Requests; die Datei
 * geht als `file` mit. Messstellen- und Verbraucherlisten werden hier NICHT
 * gespeichert – dafür bleibt der bestehende `InbetriebnahmeService`
 * (`/api/inbetriebnahme/import`) zuständig. Authentifizierung und Basisadresse
 * kommen wie bei allen Services aus Interceptor und `environment`.
 */
@Injectable({ providedIn: 'root' })
export class SpsImportApiService {
  private readonly baseUrl = environment.apiBaseUrl + '/sps';

  constructor(private http: HttpClient) { }


  // =========================================================
  // ERKENNUNG UND VORSCHAU (speichern nichts)
  // =========================================================

  /**
   * Listenart einer Excel-Datei erkennen (alle vier Typen).
   * `kiVerwenden` befragt für Messstellen-/Verbraucherlisten zusätzlich die bestehende KI.
   */
  erkennen(file: File, kiVerwenden = false): Observable<ListenErkennung> {
    const form = new FormData();
    form.append('file', file, file.name);
    form.append('kiVerwenden', String(kiVerwenden));

    return this.http.post<ListenErkennung>(`${this.baseUrl}/import/erkennen`, form);
  }

  /**
   * Vorschau einer SPS-Liste nach bestätigtem Typ (SpsZuordnungsliste | PlcTagListe).
   * @param projektId nur zur Zugriffsprüfung, gespeichert wird nichts
   */
  vorschau(
    file: File,
    listenTyp: ProjektListenTyp,
    optionen?: {
      sheetName?: string | null; maxZeilen?: number | null; projektId?: number | null; seite?: number;
      seitengroesse?: number;
    }
  ): Observable<SpsImportVorschau> {
    const form = new FormData();
    form.append('file', file, file.name);
    form.append('listenTyp', listenTyp);
    if (optionen?.seite != null) {
      form.append('seite', String(optionen.seite));
    }

    if (optionen?.seitengroesse != null) {
      form.append('seitengroesse', String(optionen.seitengroesse));
    }

    if (optionen?.sheetName) {
      form.append('sheetName', optionen.sheetName);
    }

    if (optionen?.maxZeilen != null) {
      form.append('maxZeilen', String(optionen.maxZeilen));
    }

    if (optionen?.projektId != null) {
      form.append('projektId', String(optionen.projektId));
    }

    return this.http.post<SpsImportVorschau>(`${this.baseUrl}/import/vorschau`, form);
  }


  // =========================================================
  // IMPORT
  // =========================================================

  /**
   * Eine SPS-Liste importieren (eigene Transaktion im Backend). Antwort: 201.
   * `anlagenteilSpalte` ('Ort' | 'Path') übernimmt die Zellwerte exakt als Anlagenteile des Projekts.
   */
  importieren(
    file: File,
    projektId: number,
    listenTyp: ProjektListenTyp,
    optionen?: { sheetName?: string | null; name?: string | null; anlagenteilSpalte?: SpsAnlagenteilSpalte | null }
  ): Observable<SpsImportErgebnis> {
    const form = new FormData();
    form.append('file', file, file.name);
    form.append('projektId', String(projektId));
    form.append('listenTyp', listenTyp);


    if (optionen?.sheetName) {
      form.append('sheetName', optionen.sheetName);
    }

    if (optionen?.name) {
      form.append('name', optionen.name);
    }

    if (optionen?.anlagenteilSpalte) {
      form.append('anlagenteilSpalte', optionen.anlagenteilSpalte);
    }

    return this.http.post<SpsImportErgebnis>(`${this.baseUrl}/import`, form);
  }

  /**
   * Mehrere vorbereitete SPS-Listen importieren. Position i von `files`,
   * `listenTypen`, `sheetNames` und `namen` gehört zusammen.
   *
   * Jede Datei wird im Backend in einer eigenen Transaktion gespeichert; das
   * Ergebnis nennt je Datei Erfolg oder Fehler. Nur SPS-Typen werden
   * gespeichert – Messstellen-/Verbraucherdateien lehnt das Backend ab.
   */
  importierenMehrfach(projektId: number, dateien: SpsSammelImportDatei[]): Observable<SpsSammelImportErgebnis> {
    const form = new FormData();
    form.append('projektId', String(projektId));

    // Alle Listen müssen gleich lang sein, damit der Index i stimmt.
    for (const d of dateien) {
      form.append('files', d.file, d.file.name);
      form.append('listenTypen', d.listenTyp);
      form.append('sheetNames', d.sheetName ?? '');
      form.append('namen', d.name ?? '');
      form.append('anlagenteilSpalten', d.anlagenteilSpalte ?? '');
    }

    return this.http.post<SpsSammelImportErgebnis>(`${this.baseUrl}/import/mehrfach`, form);
  }


  // =========================================================
  // LISTEN
  // =========================================================

  /** Alle SPS-Listen eines Projekts. */
  getListen(projektId: number): Observable<SpsImportListe[]> {
    const params = new HttpParams().set('projektId', projektId);
    return this.http.get<SpsImportListe[]>(`${this.baseUrl}/listen`, { params });
  }

  /** Kopfdaten einer SPS-Liste inklusive Spaltenreihenfolge der Originaldaten. */
  getListe(listeId: number): Observable<SpsImportListeDetail> {
    return this.http.get<SpsImportListeDetail>(`${this.baseUrl}/listen/${listeId}`);
  }

  /**
   * Die Anlagenteile, die in DIESER Liste tatsächlich vorkommen – Optionen für
   * den Anlagenteil-Filter von `getEintraege`, über alle Zeilen der Liste
   * (nicht nur die geladene Seite), alphabetisch und ohne Dubletten. Für die
   * Zuordnung bleibt `ProjektService.getAnlagenteile` (alle des Projekts) zuständig.
   */
  getAnlagenteile(listeId: number): Observable<SpsAnlagenteilOptionen> {
    return this.http.get<SpsAnlagenteilOptionen>(`${this.baseUrl}/listen/${listeId}/anlagenteile`);
  }

  /**
   * Originale Excel-Zeilen einer Liste – serverseitig gefiltert, sortiert und
   * seitenweise (höchstens 500 je Seite). Alle Filter sind UND-verknüpft;
   * Richtung, Prüfstatus und „nur offene“ beziehen sich auf das verknüpfte
   * gemeinsame Signal. Fehlende Werte bekommen den Standard
   * (`leererSpsListenFilter`), sodass auch der bisherige Aufruf mit nur
   * Seite, Größe und Suche weiter funktioniert.
   */
  getEintraege(listeId: number, filter?: Partial<SpsListenFilter> | null): Observable<SpsSeite<SpsImportEintrag>> {
    return this.http.get<SpsSeite<SpsImportEintrag>>(`${this.baseUrl}/listen/${listeId}/eintraege`, {
      params: SpsImportApiService.listenFilterParams({ ...leererSpsListenFilter(), ...(filter ?? {}) })
    });
  }

  /** Query-Parameter eines Listenfilters – nur gesetzte Werte, Richtung/Status/Sortierung als Backend-Enum-Namen. */
  static listenFilterParams(filter: SpsListenFilter): HttpParams {
    let params = new HttpParams()
      .set('seite', String(Math.max(1, filter.seite || 1)))
      .set('groesse', String(filter.groesse || 100));

    if (filter.sortierung && filter.sortierung !== 'zeile') { params = params.set('sortierung', filter.sortierung); }
    if (filter.nurOffen) { params = params.set('nurOffen', 'true'); }
    if (filter.richtung) { params = params.set('richtung', filter.richtung); }
    if (filter.status) { params = params.set('status', filter.status); }
    if (filter.ohneAnlagenteil) {
      params = params.set('ohneAnlagenteil', 'true');
    } else if (filter.anlagenteilId != null) {
      params = params.set('anlagenteilId', filter.anlagenteilId);
    }

    const texte: Record<string, string | null | undefined> = {
      suche: filter.suche,
      name: filter.name,
      adresse: filter.adresse,
      bmk: filter.bmk,
      beschreibung: filter.beschreibung,
      station: filter.station,
      anschluss: filter.anschluss,
      kanal: filter.kanal,
      ort: filter.ort,
      platzierung: filter.platzierung,
      datenTyp: filter.datenTyp
    };

    for (const [key, value] of Object.entries(texte)) {
      const wert = value?.trim();
      if (wert) {
        params = params.set(key, wert);
      }
    }

    return params;
  }

  /** Was das Löschen einer Liste bewirken würde – für den Bestätigungsdialog. Ändert nichts. */
  getLoeschvorschau(listeId: number): Observable<SpsListeLoeschvorschau> {
    return this.http.get<SpsListeLoeschvorschau>(`${this.baseUrl}/listen/${listeId}/loeschvorschau`);
  }

  /**
   * SPS-Liste löschen (eine Transaktion im Backend). Signale mit weiteren
   * Quellen bleiben samt Prüfung bestehen. Signale nur aus dieser Liste:
   * ungeprüfte werden entfernt, geprüfte verschwinden aus der Prüfung und
   * bleiben mit ihrem Verlauf archiviert – nur mit `mitPruefungen = true`
   * werden auch sie endgültig gelöscht (nicht Teil des normalen Ablaufs).
   */
  deleteListe(listeId: number, mitPruefungen = false): Observable<SpsListeLoeschErgebnis> {
    const params = new HttpParams().set('mitPruefungen', String(mitPruefungen));
    return this.http.delete<SpsListeLoeschErgebnis>(`${this.baseUrl}/listen/${listeId}`, { params });
  }


  // =========================================================
  // LISTENUPDATE (neue Excel-Version derselben Liste)
  // =========================================================

  /**
   * Vorschau eines Listenupdates: unverändert / aktualisiert / neu / gelöscht /
   * Konflikte / zuvor manuell gelöscht, betroffene geprüfte Signale. Speichert nichts.
   */
  updateVorschau(listeId: number, file: File, sheetName?: string | null): Observable<SpsListenUpdateVorschau> {
    const form = new FormData();
    form.append('file', file, file.name);

    if (sheetName) {
      form.append('sheetName', sheetName);
    }

    return this.http.post<SpsListenUpdateVorschau>(`${this.baseUrl}/listen/${listeId}/update/vorschau`, form);
  }

  /**
   * Listenupdate ausführen (eine Transaktion im Backend). Die Datei wird dafür ein
   * zweites Mal hochgeladen – der Server hält keinen Zustand zwischen Vorschau und
   * Ausführung. `geloeschteWiederherstellen` legt zuvor manuell gelöschte Zeilen wieder an.
   */
  updateAnwenden(
    listeId: number,
    file: File,
    optionen?: { sheetName?: string | null; geloeschteWiederherstellen?: boolean }
  ): Observable<SpsListenUpdateErgebnis> {
    const form = new FormData();
    form.append('file', file, file.name);

    if (optionen?.sheetName) {
      form.append('sheetName', optionen.sheetName);
    }

    form.append('geloeschteWiederherstellen', String(!!optionen?.geloeschteWiederherstellen));

    return this.http.post<SpsListenUpdateErgebnis>(`${this.baseUrl}/listen/${listeId}/update`, form);
  }


  // =========================================================
  // EINZELNE EINTRÄGE LÖSCHEN
  // =========================================================

  /** Was das Löschen einer Originalzeile bewirken würde (Signal bleibt / archiviert / entfernt). Ändert nichts. */
  getEintragLoeschvorschau(listeId: number, eintragId: number): Observable<SpsEintragLoeschvorschau> {
    return this.http.get<SpsEintragLoeschvorschau>(`${this.baseUrl}/listen/${listeId}/eintraege/${eintragId}/loeschvorschau`);
  }

  /**
   * Eine Originalzeile löschen (eine Transaktion im Backend). Ihr Signal bleibt,
   * wenn es weitere Quellen hat; sonst wird es ungeprüft entfernt bzw. geprüft archiviert.
   */
  deleteEintrag(listeId: number, eintragId: number): Observable<SpsLoeschErgebnis> {
    return this.http.delete<SpsLoeschErgebnis>(`${this.baseUrl}/listen/${listeId}/eintraege/${eintragId}`);
  }
}
